import { formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ApiException,
  Company,
  ListProductSubstitutionsRequest,
  NewListProductsRequest,
  Product2,
  ProductSubstitution,
  ProductType,
  ProductWorkflowState,
  ResourceType,
  SituViewWebApiClient,
  Tag,
  UpsertProductSubstitutionsRequest
} from 'situview-webapi-client';
import { ArtifactViewerDialogComponent, SelectProductDialogComponent, SelectProductDialogData, SelectProductResultData } from 'src/app/components';
import { AuthService, BreadcrumbService, CompanyViewService, ConfirmDialogService, ProductService, StateService, TaggingService } from 'src/app/services';
import { ConfigService } from 'src/app/services/config.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

/** A product as shown on a card or a row. */
interface ProductSummary {
  id: string;
  name?: string;
  sku?: string;
  manufacturerId?: string;
  previewArtifactId?: string;
  productTypeId?: string;
  disabled: boolean;
  workflowState?: ProductWorkflowState;
  calloutsEnabled?: boolean;
}

interface TagGroup {
  name: string;
  tags: Tag[];
}

type RowStatus = 'Original' | 'Add' | 'Replace';

/** A period being edited. It owns working copies of its rows until Done saves them. */
interface PeriodEdit {
  key: string;
  fromProductId: string;
  /** The saved scope and dates, which place the period until Done regroups it. */
  tagId?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  rows: ProductSubstitution[];
  /** Rows binned during the edit, removed from the card on Done. */
  removed: ProductSubstitution[];
  draftFrom: Date | null;
  draftTo: Date | null;
  draftTagId: string | null;
}

interface PeriodView {
  key: string;
  fromProductId: string;
  tagId?: string;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  rows: ProductSubstitution[];
  edit?: PeriodEdit;
  enabled: boolean;
  isNow: boolean;
  isPast: boolean;
  /** Only the band shows; the rows and the add button are hidden. */
  collapsed: boolean;
  faded: boolean;
  keepsOriginal: boolean;
  when: string;
  whenDetail: string;
  summary: string;
}

interface ScopeView {
  key: string;
  tagId?: string;
  periods: PeriodView[];
}

interface CardView {
  productId: string;
  scopes: ScopeView[];
  /** Every period has ended. */
  ended: boolean;
}

const EmptyGuid = '00000000-0000-0000-0000-000000000000';
const ProductPageSize = 500;
const UncategorisedTagGroup = 'Other';

const StatusTips: Record<RowStatus, string> = {
  Original: 'The original product is kept in this period',
  Add: 'An extra callout alongside the original product',
  Replace: 'The original is not in this period, so this replaces it'
};

/**
 * The header company's product substitutions: one card per original product, with its
 * periods grouped by home tag down a timeline. Situ employees only.
 */
@Component({
  selector: 'app-product-substitutions',
  templateUrl: './product-substitutions.component.html',
  styleUrls: ['./product-substitutions.component.scss']
})
export class ProductSubstitutionsComponent extends SituBaseComponent implements OnInit {
  readonly statusTips = StatusTips;

  cards: CardView[] = [];
  searchTerm = '';
  showPast = false;
  loaded = false;

  readonly situViewUrl: string;

  private rows: ProductSubstitution[] = [];
  private edits = new Map<string, PeriodEdit>();
  private newPeriodSeq = 0;
  private loadVersion = 0;
  private localKeys = new WeakMap<ProductSubstitution, string>();
  private localKeySeq = 0;
  private saveChains = new Map<string, Promise<void>>();

  private products = new Map<string, ProductSummary>();
  private loadingProductIds = new Set<string>();
  private placeholders = new Map<string, ProductSummary>();
  private productTypes = new Map<string, ProductType>();
  private companies = new Map<string, Company>();

  private tagGroupsSource?: Tag[];
  private tagGroupsCache: TagGroup[] = [];

  private _loading = false;
  get loading(): boolean {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewWebApiClient: SituViewWebApiClient,
    private taggingService: TaggingService,
    configService: ConfigService,
    private companyViewService: CompanyViewService,
    private breadcrumbService: BreadcrumbService,
    private confirmDialog: ConfirmDialogService,
    private dialog: MatDialog,
    private productService: ProductService,
    private snackBar: MatSnackBar,
    @Inject(LOCALE_ID) private locale: string) {
    super(authService, stateService);
    this.situViewUrl = configService.config.situViewWebApiBaseUrl;
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Product type names, and the classification that presets the catalogue.
    this.productService.LoadProductTypes()
      .then(types => this.productTypes = new Map(types.filter(t => t.id).map(t => [t.id!, t])))
      .catch(() => { });
    // Manufacturer logos, as in the theme editor.
    this.companyViewService.LoadCompanies()
      .then(companies => this.companies = new Map(companies.filter(c => c.id).map(c => [c.id!, c])))
      .catch(() => { });
  }

  protected onSelectedCompanyChange(): void {
    this.edits.clear();
    this.rows = [];
    this.loaded = false;
    this.rebuild();
    this.load();
  }

  // ---- Tags ----

  /** Home tags for the header company. */
  get homeTags(): Tag[] {
    return this.taggingService.tags?.filter(t => !t.isDeleted
      && (t.resourceType === ResourceType.Home || t.resourceType === ResourceType.Any)) ?? [];
  }

  /** Home tags grouped by category, uncategorised last. */
  get tagGroups(): TagGroup[] {
    const source = this.taggingService.tags;
    if (source === this.tagGroupsSource) return this.tagGroupsCache;
    const groups = new Map<string, TagGroup>();
    for (const tag of this.homeTags) {
      const name = tag.category?.name || UncategorisedTagGroup;
      if (!groups.has(name)) groups.set(name, { name, tags: [] });
      groups.get(name)!.tags.push(tag);
    }
    const byName = (a?: string, b?: string) => (a ?? '').localeCompare(b ?? '');
    groups.forEach(g => g.tags.sort((a, b) => byName(a.name, b.name)));
    this.tagGroupsSource = source;
    this.tagGroupsCache = [...groups.values()].sort((a, b) =>
      a.name === UncategorisedTagGroup ? 1 : b.name === UncategorisedTagGroup ? -1 : byName(a.name, b.name));
    return this.tagGroupsCache;
  }

  isHomeTag(tagId: string | null | undefined): boolean {
    return !!tagId && this.homeTags.some(t => t.id === tagId);
  }

  tagName(tagId: string | null | undefined): string {
    return (tagId && this.taggingService.tagById(tagId)?.name) || 'Home tag';
  }

  // ---- trackBy ----

  trackCard = (_index: number, card: CardView) => card.productId;
  trackScope = (_index: number, scope: ScopeView) => scope.key;
  trackPeriod = (_index: number, period: PeriodView) => period.key;
  trackRow = (_index: number, row: ProductSubstitution) => this.trackKey(row);
  trackTagGroup = (_index: number, group: TagGroup) => group.name;
  trackTag = (_index: number, tag: Tag) => tag.id;

  // ---- Toolbar ----

  onSearchChange(): void {
    this.rebuild();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.rebuild();
  }

  onShowPastChange(): void {
    this.rebuild();
  }

  /** Picks a new original product and opens a period for it. */
  addProduct(): void {
    this.openCatalogue(new SelectProductDialogData(), products => {
      const picked = products[0];
      if (!picked?.id) return;
      this.addSubstitution(picked.id);
      setTimeout(() => document.getElementById(this.cardElementId(picked.id!))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
  }

  cardElementId(productId: string): string {
    return `substitution-card-${productId}`;
  }

  // ---- Loading ----

  load(): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId) return;

    const version = ++this.loadVersion;
    this.loading = true;
    this.situViewWebApiClient.listProductSubstitutions(new ListProductSubstitutionsRequest({ builderCompanyId: companyId }))
      .then(res => {
        // A newer load or a company switch supersedes this one.
        if (version !== this.loadVersion) return;
        if (res.success) {
          this.rows = res.productSubstitutions ?? [];
          this.reconcileEdits();
          this.loaded = true;
          this.rebuild();
          this.hydrateProducts(version);
        } else {
          this.notify(`Could not load substitutions: ${this.errorText(res.errorMessages)}`);
        }
      })
      .catch(err => {
        if (version === this.loadVersion) this.notifyFailure(err, 'Failed to load product substitutions.');
      })
      .finally(() => {
        if (version === this.loadVersion) this.loading = false;
      });
  }

  /**
   * Keeps open edits across a reload: their saved rows give the reloaded rows their keys,
   * and rows that no longer exist are dropped.
   */
  private reconcileEdits(): void {
    const byId = new Map(this.rows.filter(r => r.id).map(r => [r.id!, r]));
    const keep = (r: ProductSubstitution) => {
      const fresh = r.id ? byId.get(r.id) : undefined;
      if (fresh) this.localKeys.set(fresh, this.trackKey(r));
      return !r.id || !!fresh;
    };
    this.edits.forEach(edit => {
      edit.rows = edit.rows.filter(keep);
      edit.removed = edit.removed.filter(keep);
    });
  }

  /** Loads details for every original and substitute product not yet known. */
  private async hydrateProducts(version: number): Promise<void> {
    const companyId = this.selectedCompany?.id;
    const ids = new Set<string>();
    this.rows.forEach(r => [r.fromProductId, r.toProductId].forEach(id => id && ids.add(id)));
    const missing = [...ids].filter(id => !this.products.has(id));
    if (!companyId || missing.length === 0) return;

    missing.forEach(id => this.loadingProductIds.add(id));
    try {
      for (let page = 1; ; page++) {
        const request = new NewListProductsRequest();
        request.companyId = companyId;
        request.filterProductIds = missing;
        request.includeCurrentState = true;
        request.pageNumber = page;
        request.pageSize = ProductPageSize;
        const res = await this.situViewWebApiClient.newListProducts(request);
        if (version !== this.loadVersion) return;
        if (!res.success) {
          this.notify(`Could not load product details: ${this.errorText(res.errorMessages)}`);
          break;
        }
        const items = res.items ?? [];
        items.forEach(p => this.rememberProduct(p));
        if (items.length === 0 || page * ProductPageSize >= (res.totalItems ?? 0)) break;
      }
    } catch (err) {
      if (version === this.loadVersion) this.notifyFailure(err, 'Failed to load product details.');
    } finally {
      missing.forEach(id => this.loadingProductIds.delete(id));
    }
    if (version === this.loadVersion) this.rebuild();
  }

  private rememberProduct(p: Product2): void {
    if (!p.id) return;
    this.products.set(p.id, {
      id: p.id,
      name: p.name,
      sku: p.sku,
      manufacturerId: p.companyId,
      previewArtifactId: p.previewArtifactId,
      productTypeId: p.productTypeId,
      disabled: p.disabled ?? false,
      workflowState: p.currentWorkflowState?.state,
      calloutsEnabled: p.calloutsEnabled
    });
  }

  // ---- Period actions ----

  /** Opens a new, empty period for the product, containing its self row. */
  addSubstitution(productId: string): void {
    const key = `new:${++this.newPeriodSeq}`;
    this.edits.set(key, {
      key,
      fromProductId: productId,
      rows: [this.newRow(productId, productId, undefined, undefined, undefined, true)],
      removed: [],
      draftFrom: null,
      draftTo: null,
      draftTagId: null
    });
    this.rebuild();
  }

  startEdit(period: PeriodView): void {
    if (period.edit) return;
    this.edits.set(period.key, {
      key: period.key,
      fromProductId: period.fromProductId,
      tagId: period.tagId,
      effectiveFrom: period.effectiveFrom,
      effectiveTo: period.effectiveTo,
      rows: period.rows.map(r => this.copyRow(r, {})),
      removed: [],
      draftFrom: period.effectiveFrom ? new Date(period.effectiveFrom) : null,
      draftTo: period.effectiveTo ? new Date(period.effectiveTo) : null,
      draftTagId: period.tagId ?? null
    });
    this.rebuild();
  }

  /** Applies the edited dates and tag to every row of the period, regroups and saves the card. */
  done(edit: PeriodEdit): void {
    const effectiveFrom = this.atTime(edit.draftFrom, 0, 0, 0, 0);
    const effectiveTo = this.atTime(edit.draftTo, 23, 59, 59, 999);
    if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
      this.notify('To cannot be before From.');
      return;
    }
    const tagId = edit.draftTagId || undefined;

    // The period's own rows, and any it has binned, are replaced by the edit.
    const replaced = this.editRowKeys(edit);

    // Regrouping into a period that already has one of these products would duplicate it.
    const targetKey = this.groupKey(edit.fromProductId, tagId, effectiveFrom, effectiveTo);
    const clashes = this.rows.filter(r => !replaced.has(this.trackKey(r))
      && this.groupKey(r.fromProductId!, r.tagId, r.effectiveFrom, r.effectiveTo) === targetKey
      && edit.rows.some(e => e.toProductId === r.toProductId));
    if (clashes.length) {
      const names = clashes.map(r => this.productLabel(r.toProductId!)).join(', ');
      this.notify(`A period with these dates and tag already has ${names}.`);
      return;
    }

    this.edits.delete(edit.key);
    if (edit.rows.length === 0 && replaced.size === 0) {
      this.rebuild();
      return;
    }
    this.rows = [
      ...this.rows.filter(r => !replaced.has(this.trackKey(r))),
      ...edit.rows.map(r => this.copyRow(r, { tagId, effectiveFrom, effectiveTo }))
    ];
    this.rebuild();
    this.queueSave(edit.fromProductId);
  }

  setEnabled(period: PeriodView, enabled: boolean): void {
    if (period.edit) {
      period.edit.rows = period.edit.rows.map(r => this.copyRow(r, { enabled }));
      this.rebuild();
      return;
    }
    const inPeriod = new Set(period.rows);
    this.rows = this.rows.map(r => inPeriod.has(r) ? this.copyRow(r, { enabled }) : r);
    this.rebuild();
    this.queueSave(period.fromProductId);
  }

  addToPeriod(period: PeriodView): void {
    const data = new SelectProductDialogData();
    const original = this.products.get(period.fromProductId);
    data.productTypeId = original?.productTypeId;
    data.classificationId = original?.productTypeId
      ? this.productTypes.get(original.productTypeId)?.productClassificationId
      : undefined;
    data.multiSelect = true;

    this.openCatalogue(data, products => {
      const rows = period.edit?.rows ?? period.rows;
      const existing = new Set(rows.map(r => r.toProductId));
      const picked = products.filter(p => {
        if (!p.id || existing.has(p.id)) return false;
        existing.add(p.id);
        return true;
      });
      if (picked.length === 0) {
        this.notify('Those products are already in this period.');
        return;
      }

      if (period.edit) {
        const enabled = period.edit.rows.every(r => r.enabled);
        picked.forEach(p => period.edit!.rows.push(
          this.newRow(period.fromProductId, p.id!, period.edit!.tagId, period.edit!.effectiveFrom, period.edit!.effectiveTo, enabled)));
        this.rebuild();
        return;
      }
      this.rows = [
        ...this.rows,
        ...picked.map(p => this.newRow(period.fromProductId, p.id!, period.tagId, period.effectiveFrom, period.effectiveTo, period.enabled))
      ];
      this.rebuild();
      this.queueSave(period.fromProductId);
    });
  }

  removeRow(period: PeriodView, row: ProductSubstitution): void {
    const scope = period.tagId ? this.tagName(period.tagId) : 'All homes';
    this.confirmDialog.open({
      title: 'Remove product',
      message: `Remove ${this.productLabel(row.toProductId!)} from ${scope}, ${period.when}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      actionIcon: 'delete',
      actionColour: 'warn'
    });
    this.confirmDialog.confirmed().subscribe(confirmed => {
      if (confirmed) this.applyRemoveRow(period, row);
    });
  }

  private applyRemoveRow(period: PeriodView, row: ProductSubstitution): void {
    if (period.edit) {
      period.edit.rows = period.edit.rows.filter(r => r !== row);
      period.edit.removed.push(row);
      this.rebuild();
      return;
    }
    this.rows = this.rows.filter(r => r !== row);
    this.rebuild();
    this.queueSave(period.fromProductId);
  }

  clearDraftFrom(edit: PeriodEdit): void {
    edit.draftFrom = null;
  }

  clearDraftTo(edit: PeriodEdit): void {
    edit.draftTo = null;
  }

  // ---- Saving ----

  /** Saves one card after any save already running for it, so saves on a card never race. */
  private queueSave(fromProductId: string): void {
    const version = this.loadVersion;
    const previous = this.saveChains.get(fromProductId) ?? Promise.resolve();
    const next = previous.then(() => this.saveCard(fromProductId, version));
    this.saveChains.set(fromProductId, next);
    next.then(() => {
      if (this.saveChains.get(fromProductId) === next) this.saveChains.delete(fromProductId);
    });
  }

  /**
   * Sends the card's full row list, built from local state when the save runs; the server
   * deletes any stored row left out. Returned rows replace the sent ones in place.
   */
  private async saveCard(fromProductId: string, version: number): Promise<void> {
    const companyId = this.selectedCompany?.id;
    // A reload since this save was queued has replaced the local state it was for.
    if (!companyId || version !== this.loadVersion) return;

    const sent = this.rows.filter(r => r.fromProductId === fromProductId);
    const rows = sent.map(r => this.toUpsert(r, companyId));
    const request = new UpsertProductSubstitutionsRequest({ builderCompanyId: companyId, fromProductId, productSubstitutions: rows });
    try {
      const res = await this.situViewWebApiClient.upsertProductSubstitutions(request);
      if (version !== this.loadVersion) return;
      if (res.success) {
        this.applySaved(sent, rows, res.productSubstitutions ?? []);
        this.rebuild();
        this.hydrateProducts(version);
      } else {
        this.notify(`Could not save: ${this.errorText(res.errorMessages)}`);
        this.load();
      }
    } catch (err) {
      if (version !== this.loadVersion) return;
      this.notifyFailure(err, 'Failed to save product substitutions.');
      this.load();
    }
  }

  /**
   * Swaps each sent row for its saved counterpart, matched by id or, for an insert, by product
   * and period. Rows changed or added while the save ran are left for the next save.
   */
  private applySaved(sent: ProductSubstitution[], rows: ProductSubstitution[], saved: ProductSubstitution[]): void {
    const byId = new Map(saved.filter(s => s.id).map(s => [s.id!, s]));
    const byKey = new Map(saved.map(s => [this.rowKey(s), s]));
    const replacements = new Map<ProductSubstitution, ProductSubstitution>();
    sent.forEach((local, i) => {
      const row = rows[i];
      const match = row.id && row.id !== EmptyGuid ? byId.get(row.id) : byKey.get(this.rowKey(row));
      if (!match) return;
      this.localKeys.set(match, this.trackKey(local));
      replacements.set(local, match);
    });
    this.rows = this.rows.map(r => replacements.get(r) ?? r);
  }

  private rowKey(row: ProductSubstitution): string {
    return `${this.groupKey(row.fromProductId!, row.tagId, row.effectiveFrom, row.effectiveTo)}|${row.toProductId}`;
  }

  private trackKey(row: ProductSubstitution): string {
    return this.localKeys.get(row) ?? row.id ?? '';
  }

  private newRow(fromProductId: string, toProductId: string, tagId: string | undefined,
    effectiveFrom: Date | undefined, effectiveTo: Date | undefined, enabled: boolean): ProductSubstitution {
    const row = new ProductSubstitution({
      builderCompanyId: this.selectedCompany?.id,
      fromProductId,
      toProductId,
      tagId,
      effectiveFrom,
      effectiveTo,
      enabled
    } as ProductSubstitution);
    this.localKeys.set(row, `local:${++this.localKeySeq}`);
    return row;
  }

  /** A changed copy that keeps the row's trackBy key; notes and other fields carry over. */
  private copyRow(row: ProductSubstitution, changes: Partial<ProductSubstitution>): ProductSubstitution {
    const copy = new ProductSubstitution({ ...row, ...changes } as ProductSubstitution);
    const key = this.trackKey(row);
    if (key) this.localKeys.set(copy, key);
    return copy;
  }

  /** The row as sent; every row carries the request's builder and original product. */
  private toUpsert(row: ProductSubstitution, builderCompanyId: string): ProductSubstitution {
    return new ProductSubstitution({
      ...row,
      id: row.id || EmptyGuid,
      builderCompanyId
    } as ProductSubstitution);
  }

  /** The trackBy keys of the rows an edit has taken over from its period. */
  private editRowKeys(edit?: PeriodEdit): Set<string> {
    const keys = new Set<string>();
    const add = (e: PeriodEdit) => [...e.rows, ...e.removed].forEach(r => keys.add(this.trackKey(r)));
    if (edit) add(edit);
    else this.edits.forEach(add);
    return keys;
  }

  // ---- Grouping ----

  /** Builds the cards from the raw rows and the open edits. */
  private rebuild(): void {
    const now = new Date();
    const editKeysTaken = this.editRowKeys();
    const editKeys = new Set(this.edits.keys());

    const groups = new Map<string, ProductSubstitution[]>();
    for (const row of this.rows) {
      if (!row.fromProductId || !row.toProductId || editKeysTaken.has(this.trackKey(row))) continue;
      const key = this.groupKey(row.fromProductId, row.tagId, row.effectiveFrom, row.effectiveTo);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    }

    const byProduct = new Map<string, PeriodView[]>();
    const add = (period: PeriodView) => {
      if (!byProduct.has(period.fromProductId)) byProduct.set(period.fromProductId, []);
      byProduct.get(period.fromProductId)!.push(period);
    };
    groups.forEach((rows, key) => {
      const first = rows[0];
      // An edit keeps the key its period had, so a new period with the same values needs another.
      add(this.periodView(editKeys.has(key) ? `${key}#` : key, first.fromProductId!, first.tagId,
        first.effectiveFrom, first.effectiveTo, rows, undefined, now));
    });
    this.edits.forEach(edit => add(this.periodView(edit.key, edit.fromProductId, edit.tagId,
      edit.effectiveFrom, edit.effectiveTo, edit.rows, edit, now)));

    const term = this.searchTerm.trim().toLowerCase();
    const cards: CardView[] = [];
    byProduct.forEach((periods, productId) => {
      const visible = periods.filter(p => p.edit || p.rows.length > 0);
      if (visible.length === 0) return;
      const ended = !visible.some(p => p.edit || !p.isPast);
      if (ended && !this.showPast) return;
      if (term && !this.matches(productId, term)
        && !visible.some(p => p.rows.some(r => this.matches(r.toProductId!, term)))) return;

      const scopes = new Map<string, ScopeView>();
      for (const period of visible) {
        const key = period.tagId ?? '';
        if (!scopes.has(key)) scopes.set(key, { key, tagId: period.tagId, periods: [] });
        scopes.get(key)!.periods.push(period);
      }
      scopes.forEach(s => s.periods.sort((a, b) =>
        this.time(a.effectiveFrom, -Infinity) - this.time(b.effectiveFrom, -Infinity)
        || this.time(a.effectiveTo, Infinity) - this.time(b.effectiveTo, Infinity)
        || a.key.localeCompare(b.key)));
      const sortedScopes = [...scopes.values()].sort((a, b) =>
        !a.tagId ? -1 : !b.tagId ? 1 : this.tagName(a.tagId).localeCompare(this.tagName(b.tagId)));

      cards.push({ productId, scopes: sortedScopes, ended });
    });

    cards.sort((a, b) => this.productLabel(a.productId).localeCompare(this.productLabel(b.productId))
      || a.productId.localeCompare(b.productId));
    this.cards = cards;
  }

  private periodView(key: string, fromProductId: string, tagId: string | undefined,
    effectiveFrom: Date | undefined, effectiveTo: Date | undefined,
    rows: ProductSubstitution[], edit: PeriodEdit | undefined, now: Date): PeriodView {
    const enabled = rows.every(r => r.enabled);
    const isPast = !edit && !!effectiveTo && effectiveTo < now;
    const isNow = enabled && (!effectiveFrom || effectiveFrom <= now) && (!effectiveTo || effectiveTo >= now);
    const keepsOriginal = rows.some(r => r.toProductId === fromProductId);
    const others = rows.filter(r => r.toProductId !== fromProductId).map(r => this.productShortLabel(r.toProductId!));

    let when = 'Always';
    let whenDetail = 'no start or end';
    if (effectiveFrom && effectiveTo) {
      when = `${this.formatDay(effectiveFrom)} to ${this.formatDay(effectiveTo)}`;
      whenDetail = '';
    } else if (effectiveFrom) {
      when = `From ${this.formatDay(effectiveFrom)}`;
      whenDetail = 'no end';
    } else if (effectiveTo) {
      when = `Until ${this.formatDay(effectiveTo)}`;
      whenDetail = '';
    }

    return {
      key, fromProductId, tagId: tagId || undefined, effectiveFrom, effectiveTo, rows, edit,
      enabled, isNow, isPast, keepsOriginal, when, whenDetail,
      collapsed: isPast && !this.showPast,
      faded: !enabled || isPast,
      summary: others.length === 0
        ? 'Kept the original'
        : `${keepsOriginal ? 'Added' : 'Replaced with'} ${others.join(' + ')}`
    };
  }

  /** Periods are grouped by tag and by their exact stored dates. */
  private groupKey(fromProductId: string, tagId: string | undefined, from: Date | undefined, to: Date | undefined): string {
    return [fromProductId, tagId || '', from?.getTime() ?? '', to?.getTime() ?? ''].join('|');
  }

  private time(date: Date | undefined, fallback: number): number {
    return date ? date.getTime() : fallback;
  }

  private matches(productId: string, term: string): boolean {
    const product = this.products.get(productId);
    return !!product && ((product.name ?? '').toLowerCase().includes(term)
      || (product.sku ?? '').toLowerCase().includes(term));
  }

  rowStatus(period: PeriodView, row: ProductSubstitution): RowStatus {
    if (row.toProductId === period.fromProductId) return 'Original';
    return period.keepsOriginal ? 'Add' : 'Replace';
  }

  statusChipClass(status: RowStatus): string {
    switch (status) {
      case 'Add': return 'blue-chip';
      case 'Replace': return 'green-chip';
      default: return '';
    }
  }

  // ---- Product display ----

  isProductLoaded(id: string | undefined): boolean {
    return !!id && this.products.has(id);
  }

  isProductLoading(id: string | undefined): boolean {
    return !!id && this.loadingProductIds.has(id);
  }

  productOf(id: string | undefined): ProductSummary {
    id = id ?? '';
    const product = this.products.get(id);
    if (product) return product;
    if (!this.placeholders.has(id)) this.placeholders.set(id, { id, disabled: false });
    return this.placeholders.get(id)!;
  }

  private productLabel(id: string): string {
    return this.products.get(id)?.name ?? id;
  }

  private productShortLabel(id: string): string {
    const product = this.products.get(id);
    return product?.sku || product?.name || 'a product';
  }

  productTypeName(product: ProductSummary): string | undefined {
    return product.productTypeId ? this.productTypes.get(product.productTypeId)?.name : undefined;
  }

  /** No workflow state means there is nothing to flag, as in the theme editor. */
  isNotLive(product: ProductSummary): boolean {
    return product.workflowState !== undefined && product.workflowState !== ProductWorkflowState.Live;
  }

  companyLogo(product: ProductSummary): string | undefined {
    return product.manufacturerId ? this.companies.get(product.manufacturerId)?.logoUrl : undefined;
  }

  companyName(product: ProductSummary): string | undefined {
    return product.manufacturerId ? this.companies.get(product.manufacturerId)?.name : undefined;
  }

  onView3dPreview(product: ProductSummary): void {
    if (!product.previewArtifactId) return;
    this.dialog.open(ArtifactViewerDialogComponent, {
      width: `${Math.round(0.7 * window.innerWidth)}px`,
      height: `${Math.round(0.9 * window.innerHeight)}px`,
      data: {
        fileName: 'product.glb',
        displayName: product.name,
        artifactId: product.previewArtifactId,
        additionalFileNames: ArtifactViewerDialogComponent.productPreviewFiles,
      }
    });
  }

  private openCatalogue(data: SelectProductDialogData, onPicked: (products: Product2[]) => void): void {
    const ref = this.dialog.open(SelectProductDialogComponent, {
      width: '80vw',
      height: '80vh',
      data,
      autoFocus: false
    });
    ref.afterClosed().subscribe((result: SelectProductResultData | undefined) => {
      if (!result?.products?.length) return;
      result.products.forEach(p => this.rememberProduct(p));
      onPicked(result.products);
    });
  }

  // ---- Helpers ----

  /** A copy of a picked day at the given local time; the picked value is left as is. */
  private atTime(picked: Date | null, hours: number, minutes: number, seconds: number, ms: number): Date | undefined {
    if (!picked) return undefined;
    const date = new Date(picked);
    date.setHours(hours, minutes, seconds, ms);
    return date;
  }

  private formatDay(date: Date): string {
    return formatDate(date, 'mediumDate', this.locale);
  }

  private errorText(errors: string[] | undefined): string {
    return errors?.length ? errors.join('; ') : 'unknown error';
  }

  /** Shows the server's reason for a rejected call where there is one, such as a 401. */
  private notifyFailure(err: unknown, fallback: string): void {
    if (err instanceof ApiException && err.status === 401) {
      this.notify(this.apiExceptionText(err) || 'Unauthorized for this company.');
    } else {
      this.notify(fallback);
    }
  }

  private apiExceptionText(err: ApiException): string {
    const text = (err.response ?? '').trim();
    try {
      const body = JSON.parse(text);
      if (typeof body === 'string') return body;
      if (Array.isArray(body?.errorMessages)) return body.errorMessages.join('; ');
    } catch {
      // Not JSON; use the text as is.
    }
    return text;
  }

  private notify(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
